import React from 'react';
import PropTypes from 'prop-types';
import { Context } from '@front10/helpers/dist/Context';
import Input from '../../elements/Input';
import Text from '../../elements/Text';

class LocationSelectorInput extends React.Component {
  constructor(props) {
    super(props);
    this.selectorInputRef = React.createRef(false);
    this.state = {
      rightIcons: this.getRightIcons(),
      rightAriaLabel: []
    };
  }

  componentDidMount() {
    this.setState({ rightAriaLabel: this.getRightAriaLabels() });
  }

  componentDidUpdate = (prevProps) => {
    this.onUpdate(prevProps);
  };

  onUpdate = (prevProps) => {
    const { isOpen, clearable } = this.props;
    if (prevProps.isOpen !== isOpen || prevProps.clearable !== clearable) {
      this.setState({
        rightIcons: this.getRightIcons(),
        rightAriaLabel: this.getRightAriaLabels()
      });
    }
  };

  onChange = (event) => {
    const { onChange } = this.props;
    onChange(event);
  };

  focusSelectorInput = () => {
    if (this.selectorInputRef.current) {
      this.selectorInputRef.current.focus();
    }
  };

  onRightIconClick = (icon) => {
    const {
      onClear,
      onOpenMenu,
      onCloseMenu,
      isOpen,
      loading,
      iconArrowUp,
      iconArrowDown,
      iconClose,
      isRightIconClickable,
      focusReferences,
      isMobile
    } = this.props;
    if (!loading) {
      const { rightIcons } = this.state;
      if (icon === iconClose) {
        onClear();
        if (!isMobile) {
          focusReferences?.current?.focus();
        }
      } else {
        if (isRightIconClickable) {
          rightIcons[rightIcons.length - 1] = isOpen ? iconArrowDown : iconArrowUp;
          this.setState({ rightIcons });
          if (isOpen) {
            onCloseMenu();
          } else {
            onOpenMenu();
          }
        }
      }
    }
  };

  getRightIcons = () => {
    const {
      clearable,
      showArrowsIfClearable,
      isOpen,
      showArrows,
      iconArrowUp,
      iconArrowDown,
      iconClose,
      customClearInputButton
    } = this.props;
    let icons = [];
    if (showArrows) {
      icons = !isOpen ? [iconArrowDown] : [iconArrowUp];
    }
    if (customClearInputButton) {
      if (clearable) {
        if (showArrowsIfClearable && showArrows) {
          if (!isOpen) {
            icons = [iconArrowDown];
          } else {
            icons = [iconArrowUp];
          }
        } else {
          icons = [];
        }
      }
    } else {
      if (clearable) {
        icons = [iconClose];
      }
    }
    return icons;
  };

  getRightAriaLabels = () => {
    const { clearable, isOpen, showArrows, isRightIconClickable } = this.props;
    const { translate } = this.context;
    let labels = [];
    if (showArrows) {
      if (isRightIconClickable) {
        labels = !isOpen
          ? [
              translate(
                'cmp-location-aria-label-icon-keyboard-arrow-down',
                'Open dropdown. You can also press Arrow Down key in the input field.'
              )
            ]
          : [
              translate(
                'cmp-location-aria-label-icon-keyboard-arrow-up',
                'Close dropdown. You can also press Escape key.'
              )
            ];
      } else {
        labels = !isOpen
          ? [
              translate(
                'cmp-location-aria-label-icon-keyboard-arrow-down-disabled',
                'Dropdown indicator. Use Arrow Down key in the input field to open suggestions.'
              )
            ]
          : [
              translate(
                'cmp-location-aria-label-icon-keyboard-arrow-up-disabled',
                'Dropdown open indicator. Use Escape key to close.'
              )
            ];
      }
    }
    if (clearable) {
      labels = [translate('cmp-location-aria-label-icon-close', 'Clear filter')];
    }
    return labels;
  };

  getRightIconsProps = () => {
    const { loading } = this.props;
    const { translate } = this.context;
    return {
      value: loading
        ? translate('cmp-location-aria-label-icon-loading', 'Loading')
        : translate('cmp-location-aria-label-icon-close', 'Clear filter')
    };
  };

  render = () => {
    const { rightIcons, rightAriaLabel } = this.state;
    const {
      clearable,
      ToggleElement = Input,
      value,
      count,
      characterLimit = 17,
      labelSeparator
    } = this.props;
    const rightTabIndex = clearable ? [0] : this.props.isRightIconClickable ? [0] : [-1];
    const {
      icon,
      loading,
      showErrorLabel,
      errorLabelKey,
      errorLabel,
      rightIconsProps,
      rootProps,
      makeAccessibleByArrowsKeys
    } = this.props;
    const { translate } = this.context;

    const customAriaLabel = this.props.showLabel
      ? this.props.ariaLabelledBy
        ? this.props.ariaLabelledBy
        : rootProps?.['aria-labelledby'] && `label-${rootProps?.['aria-labelledby']}`
      : undefined;

    let comboBoxProps = {
      ...rootProps,
      'aria-labelledby': customAriaLabel,
      'aria-describedby': this.props.ariaDescribedBy
    };

    if (makeAccessibleByArrowsKeys || (this.props.showLabelInside && this.props.useInput)) {
      comboBoxProps['role'] = null;
      comboBoxProps['aria-expanded'] = null;
      comboBoxProps['aria-controls'] = null;
      comboBoxProps['aria-haspopup'] = null;
      comboBoxProps['aria-labelledby'] = null;
      comboBoxProps['aria-owns'] = null;
    }

    const accessibilityButtonSettings = {
      'aria-expanded': this.props.isOpen
    };

    let comboboxSettings = null;
    if (rootProps['role'] === 'combobox') {
      comboboxSettings = {
        'aria-expanded': this.props.isOpen,
        'aria-controls': this.props['aria-controls'],
        'aria-haspopup': 'listbox'
      };
    }
    const countValue =
      typeof value === 'string' && count
        ? value.split(labelSeparator !== undefined ? labelSeparator : ', ').length
        : 0;

    return !this.props.useInput ? (
      <div ref={rootProps.ref}>
        <div ref={this.props.locationSelectorInputRef}>
          <ToggleElement
            ref={this.selectorInputRef}
            leftIcons={icon ? [icon] : []}
            leftAriaLabel={icon ? [icon] : []}
            rightIcons={!loading ? rightIcons : ['spinner']}
            rightAriaLabel={!loading ? rightAriaLabel : ['Loading']}
            onRightIconClick={this.onRightIconClick}
            leftTabIndex={[-1]}
            rightTabIndex={rightTabIndex}
            autoCorrect="off"
            autoComplete="off"
            spellCheck="off"
            ariaAutoComplete="list"
            {...this.props}
            value={
              ToggleElement === Input && count && countValue > 1
                ? `${value.slice(0, characterLimit)}...(${countValue})`
                : this.props.value
            }
            rightIconsProps={{ ...rightIconsProps, ...this.getRightIconsProps() }}
            makeAccessibleByArrowsKeysRole={rootProps['role']}
            comboBoxProps={comboBoxProps}
            aria-controls={rootProps['aria-owns']}
            {...accessibilityButtonSettings}
          />
          {/* {ToggleElement === Input && count && countValue > 1 && (
            <Text className="fr-count-interest" as="p" {...countSettings}>
              ({countValue})
            </Text>
          )} */}
        </div>
        {showErrorLabel && <Text {...errorLabel}>{translate(errorLabelKey, errorLabelKey)}</Text>}
      </div>
    ) : (
      <div aria-controls={this.props['aria-controls']} {...comboBoxProps} ref={rootProps.ref}>
        <ToggleElement
          ref={this.selectorInputRef}
          leftIcons={icon ? [icon] : []}
          leftAriaLabel={icon ? [icon] : []}
          rightIcons={!loading ? rightIcons : ['spinner']}
          rightAriaLabel={!loading ? rightAriaLabel : ['Loading']}
          onRightIconClick={this.onRightIconClick}
          leftTabIndex={[-1]}
          rightTabIndex={rightTabIndex}
          autoCorrect="off"
          autoComplete="off"
          spellCheck="off"
          ariaAutoComplete="list"
          {...this.props}
          rightIconsProps={{ ...rightIconsProps, ...this.getRightIconsProps() }}
          makeAccessibleByArrowsKeysRole={rootProps['role']}
          comboboxSettings={!comboBoxProps['role'] ? comboboxSettings : null}
        />
        {showErrorLabel && <Text {...errorLabel}>{translate(errorLabelKey, errorLabelKey)}</Text>}
      </div>
    );
  };
}

LocationSelectorInput.propTypes = {
  /**
   * Props of rightIcons
   */
  rightIconsProps: PropTypes.objectOf(PropTypes.any),
  /**
   * Props of rightIcons
   */
  rootProps: PropTypes.objectOf(PropTypes.any),
  /**
   * Setting of errorLabel
   */
  errorLabel: PropTypes.objectOf(PropTypes.any),
  /**
   * label key to translate
   */
  errorLabelKey: PropTypes.string,
  /**
   * Define if show error label
   */
  showErrorLabel: PropTypes.bool,
  clearable: PropTypes.bool,
  isOpen: PropTypes.bool,
  onChange: PropTypes.func,
  onClear: PropTypes.func,
  onOpenMenu: PropTypes.func,
  onCloseMenu: PropTypes.func,
  loading: PropTypes.bool,
  icon: PropTypes.string,
  showArrowsIfClearable: PropTypes.bool,
  showArrows: PropTypes.bool,
  /**
   * Data test ID of buttom
   */
  dataTestId: PropTypes.string,
  translate: PropTypes.func,
  /**
   * aria-activedescendant
   */
  ariaActivedescendant: PropTypes.string,
  /**
   * Icon show when Location is close
   */
  iconArrowDown: PropTypes.string,
  /**
   * Icon show when Location is open
   */
  iconArrowUp: PropTypes.string,
  /**
   * Icon close show in the input
   */
  iconClose: PropTypes.string,
  /**
   *  Determine if the right button action wil be executed
   */
  isRightIconClickable: PropTypes.bool,
  rightButtonIconsProps: PropTypes.objectOf(PropTypes.any),
  isMobile: PropTypes.bool,
  customClearInputButton: PropTypes.bool,
  useInput: PropTypes.bool
};

LocationSelectorInput.defaultProps = {
  rightIconsProps: {},
  showErrorLabel: false,
  errorLabelKey: '',
  errorLabel: {
    textColor: 'danger'
  },
  iconArrowDown: 'keyboard_arrow_down',
  iconArrowUp: 'keyboard_arrow_up',
  iconClose: 'close',
  dataTestId: '',
  clearable: false,
  isOpen: false,
  onChange: () => {},
  onClear: () => {},
  onOpenMenu: () => {},
  onCloseMenu: () => {},
  loading: false,
  icon: null,
  showArrowsIfClearable: true,
  showArrows: true,
  translate: () => {},
  ariaActivedescendant: null,
  rootProps: null,
  isRightIconClickable: true,
  customClearInputButton: false,
  useInput: true,
  count: false,
  labelSeparator: ', '
};

LocationSelectorInput.contextType = Context;

export default LocationSelectorInput;
